import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import logo from "../assets/logo2.png";
import bggImg from "../assets/hospital.jpeg";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import {
  FaSearch,
  FaPlus,
  FaMinus,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaChevronDown,
  FaShoppingBag,
} from "react-icons/fa";
import axios from "axios";

const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Services", href: "#services" },
  { label: "Track Transaction", href: "/track" },
  { label: "Support", href: "/support" },
  { label: "Feedback", href: "/feedback" },
];

const fetchHospitalDept = async () => {
  const res = await axios.get("https://pay1one.com/api/get/203/hospital-dept");
  if (!res.data.success) throw new Error("Failed");
  return res.data.data.orgDepts;
};

const fetchHospitalRecords = async (deptId) => {
  if (!deptId || deptId === "all") return [];
  const res = await axios.get(
    `https://pay1one.com/api/get/203/${deptId}/hospital-revenuehead`,
  );
  if (!res.data.success) throw new Error("Failed");
  return res.data.data;
};

const fetchServices = async ({ dept, search }) => {
  if (search && search.trim()) {
    const url = dept
      ? `https://pay1one.com/api/get/203/search-hospital-records?query=${encodeURIComponent(search)}&department_id=${dept}`
      : `https://pay1one.com/api/get/203/search-hospital-records?query=${encodeURIComponent(search)}`;
    const res = await axios.get(url);
    if (!res.data.success) return [];
    return res.data.data;
  }
  return fetchHospitalRecords(dept);
};

export default function Home() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickPayOpen, setQuickPayOpen] = useState(false);
  const dropdownRef = useRef(null);

  // QuickPay modal state
  const [selectedDept, setSelectedDept] = useState("");
  const [deptSearch, setDeptSearch] = useState("");
  const [deptOpen, setDeptOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [quantities, setQuantities] = useState({});
  const [customPrices, setCustomPrices] = useState({});

  const { data: departments = [], isLoading: deptsLoading } = useQuery({
    queryKey: ["homeDepts"],
    queryFn: fetchHospitalDept,
    enabled: quickPayOpen,
    staleTime: 10 * 60 * 1000,
  });

  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["homeServices", selectedDept, serviceSearch],
    queryFn: () =>
      serviceSearch.trim().length > 1
        ? fetchServices({ dept: selectedDept, search: serviceSearch })
        : fetchHospitalRecords(selectedDept),
    enabled:
      quickPayOpen && (!!selectedDept || serviceSearch.trim().length > 1),
  });

  const filteredDepts = departments.filter((d) =>
    d.name.toLowerCase().includes(deptSearch.toLowerCase()),
  );
  const filteredServices = services.filter((s) =>
    s.name.toLowerCase().includes(serviceSearch.toLowerCase()),
  );

  const totalCost = Object.entries(selectedItems).reduce((sum, [id, item]) => {
    const price = parseFloat(customPrices[id] || item.price || 0);
    const qty = quantities[id] || 1;
    return sum + price * qty;
  }, 0);

  const handleToggleItem = (svc) => {
    setSelectedItems((prev) => {
      if (prev[svc.id]) {
        const n = { ...prev };
        delete n[svc.id];
        return n;
      }
      return { ...prev, [svc.id]: svc };
    });
    if (!quantities[svc.id]) setQuantities((p) => ({ ...p, [svc.id]: 1 }));
  };

  const handleQty = (id, delta) => {
    setQuantities((p) => ({ ...p, [id]: Math.max(1, (p[id] || 1) + delta) }));
  };

  const handleProceed = () => {
    if (!Object.keys(selectedItems).length) return;
    Object.entries(selectedItems).forEach(([id, svc]) => {
      const price = parseFloat(customPrices[id] || svc.price || 0);
      const qty = quantities[id] || 1;
      addToCart({
        id: svc.id,
        name: svc.name,
        price,
        quantity: qty,
        description: svc.description,
      });
    });
    setQuickPayOpen(false);
    navigate("/receipt");
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100">
              <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">
                UCH Self Service
              </p>
              <p className="text-gray-400 text-[10px]">
                University College Hospital
              </p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) =>
              l.href.startsWith("/") ? (
                <a
                  key={l.label}
                  href={l.href}
                  className="text-sm text-gray-600 hover:text-[#0d3d2e] font-medium transition"
                >
                  {l.label}
                </a>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  className="text-sm text-gray-600 hover:text-[#0d3d2e] font-medium transition"
                >
                  {l.label}
                </a>
              ),
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => navigate("/login")}
              className="text-sm font-semibold text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition"
            >
              Sign in
            </button>
            <button
              onClick={() => setQuickPayOpen(true)}
              className="text-sm font-semibold text-[#0d3d2e] border-2 border-[#0d3d2e] hover:bg-green-50 px-4 py-2 rounded-lg transition flex items-center gap-1.5"
            >
              ⚡ Quick Pay
            </button>
            <button
              onClick={() => navigate("/register")}
              className="text-sm font-semibold text-white bg-[#0d3d2e] hover:bg-[#0a3226] px-4 py-2 rounded-lg transition"
            >
              Create wallet
            </button>
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <div className="space-y-1.5">
              <span className="block w-5 h-0.5 bg-gray-700" />
              <span className="block w-5 h-0.5 bg-gray-700" />
              <span className="block w-5 h-0.5 bg-gray-700" />
            </div>
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t px-6 py-4 space-y-3">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm text-gray-700 py-1"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => navigate("/login")}
              className="w-full text-sm border border-gray-200 rounded-lg py-2"
            >
              Sign in
            </button>
            <button
              onClick={() => {
                setMobileOpen(false);
                setQuickPayOpen(true);
              }}
              className="w-full text-sm border-2 border-[#0d3d2e] text-[#0d3d2e] font-semibold rounded-lg py-2"
            >
              ⚡ Quick Pay
            </button>
            <button
              onClick={() => navigate("/register")}
              className="w-full text-sm bg-[#0d3d2e] text-white rounded-lg py-2"
            >
              Create wallet
            </button>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="relative min-h-[92vh] flex items-center justify-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={bggImg}
            alt="UCH Hospital"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#0d3d2e]/70" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-6">
            Care begins before
            <br />
            you reach our <span className="text-green-400">doors.</span>
          </h1>
          <p className="text-white/80 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            The University College Hospital, Ibadan now lets you handle every
            payment from wherever you are — securely, instantly, and without the
            queue.
          </p>
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-white font-black text-2xl">UCH</p>
              <p className="text-white/60 text-xs uppercase tracking-widest">
                Official Portal
              </p>
            </div>
            <div className="w-px h-10 bg-white/30" />
            <div className="text-center">
              <p className="text-white font-black text-2xl">24/7</p>
              <p className="text-white/60 text-xs uppercase tracking-widest">
                Always available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Get Started ── */}
      <section className="py-24 bg-[#f5f5f0]" id="services">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-3">
              GET STARTED
            </p>
            <h2 className="text-4xl font-black text-gray-900 mb-3">
              What would you like to do?
            </h2>
            <p className="text-gray-500">
              Choose an option below to get started with the UCH Self Service
              portal.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Pay */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8 hover:shadow-lg transition-all group relative">
              <div className="absolute top-6 right-6">
                <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-100 px-3 py-1 rounded-full">
                  No login needed
                </span>
              </div>
              <div className="w-12 h-12 bg-[#0d3d2e] rounded-xl flex items-center justify-center mb-5">
                <span className="text-white text-xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Quick Pay
              </h3>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                Pay for a hospital service instantly without creating an
                account, fast, direct, and secure.
              </p>
              <ul className="space-y-2 mb-8">
                {[
                  "No registration required",
                  "Instant payment confirmation",
                  "Digital receipt to your email",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-2.5 text-sm text-gray-600"
                  >
                    <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-green-700 text-[10px]">✓</span>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setQuickPayOpen(true)}
                className="w-full bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold py-3 rounded-xl transition text-sm"
              >
                Pay now
              </button>
            </div>

            {/* Create Wallet */}
            <div className="bg-[#0d3d2e] rounded-2xl p-8 hover:shadow-lg transition-all group relative">
              <div className="absolute top-6 right-6">
                <span className="text-xs font-semibold text-green-400 bg-green-900/50 border border-green-800 px-3 py-1 rounded-full">
                  Recommended
                </span>
              </div>
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-5">
                <span className="text-white text-xl">👛</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Create Wallet
              </h3>
              <p className="text-green-200 text-sm mb-6 leading-relaxed">
                Set up your personal UCH wallet to fund once and pay for any
                service anytime — faster every visit.
              </p>
              <ul className="space-y-2 mb-8">
                {[
                  "Fund wallet once, pay anytime",
                  "Full transaction history",
                  "Manage all payments in one place",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-center gap-2.5 text-sm text-green-200"
                  >
                    <div className="w-4 h-4 bg-green-500/30 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-green-400 text-[10px]">✓</span>
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/register")}
                className="w-full bg-white hover:bg-gray-100 text-[#0d3d2e] font-semibold py-3 rounded-xl transition text-sm"
              >
                Create free account
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="py-24 bg-white" id="how-it-works">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-3">
              HOW IT WORKS
            </p>
            <h2 className="text-4xl font-black text-gray-900 mb-3">
              Three steps to seamless care
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              No queues, no hassle. Manage your hospital payments from your
              phone or computer.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                icon: "👤",
                title: "Create your account",
                desc: "Register with your email and set up your secure UCH self service account in minutes.",
              },
              {
                step: "02",
                icon: "💰",
                title: "Fund your wallet",
                desc: "Top up your wallet once and use it to pay for any hospital service instantly.",
              },
              {
                step: "03",
                icon: "🧾",
                title: "Pay for services",
                desc: "Select the service you need, confirm payment, and receive your receipt digitally.",
              },
              {
                step: "04",
                icon: "📊",
                title: "Track everything",
                desc: "View your full transaction history and wallet balance from your personal dashboard.",
              },
            ].map((s) => (
              <div
                key={s.step}
                className="bg-[#f5f5f0] rounded-2xl p-6 relative"
              >
                <p className="text-green-700/30 text-xs font-bold uppercase tracking-widest mb-4">
                  STEP {s.step}
                </p>
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-xl mb-4 shadow-sm">
                  {s.icon}
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">
                  {s.title}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="py-24 bg-[#f5f5f0]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-3">
              WHAT YOU CAN PAY FOR
            </p>
            <h2 className="text-4xl font-black text-gray-900 mb-3">
              Hospital services at your fingertips
            </h2>
            <p className="text-gray-500">
              Access a full range of UCH services online without visiting the
              billing counter.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              {
                icon: "🩺",
                name: "Consultations",
                desc: "Pay for outpatient and specialist consultation fees quickly.",
              },
              {
                icon: "💊",
                name: "Pharmacy",
                desc: "Purchase prescribed medications directly through the portal.",
              },
              {
                icon: "🔬",
                name: "Laboratory",
                desc: "Pay for lab tests and access your results with ease.",
              },
              {
                icon: "☢️",
                name: "Radiology",
                desc: "Settle X-ray, MRI, and scan fees without the queue.",
              },
              {
                icon: "🏥",
                name: "Admissions",
                desc: "Pay admission and ward fees securely from anywhere.",
              },
              {
                icon: "📋",
                name: "Medical reports",
                desc: "Request and pay for official medical documentation online.",
              },
            ].map((s) => (
              <div
                key={s.name}
                className="bg-white rounded-2xl p-5 hover:shadow-md transition group cursor-default"
              >
                <div className="w-10 h-10 bg-[#f5f5f0] rounded-xl flex items-center justify-center text-xl mb-3 group-hover:bg-green-50 transition">
                  {s.icon}
                </div>
                <h3 className="font-bold text-gray-900 text-xs mb-1">
                  {s.name}
                </h3>
                <p className="text-gray-400 text-[11px] leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-[#0d3d2e] rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-green-400 -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-green-600 translate-y-1/2 -translate-x-1/2" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                Ready to take control of your healthcare payments?
              </h2>
              <p className="text-green-200 mb-8 text-sm">
                Join thousands of UCH patients managing their bills digitally —
                faster, safer, and from anywhere.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <button
                  onClick={() => navigate("/login")}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/30 font-semibold px-6 py-3 rounded-xl transition text-sm"
                >
                  Sign in now
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="bg-white hover:bg-gray-100 text-[#0d3d2e] font-semibold px-6 py-3 rounded-xl transition text-sm"
                >
                  Create a free account
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-950 text-gray-400 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center overflow-hidden">
                  <img
                    src={logo}
                    alt="UCH"
                    className="w-7 h-7 object-contain"
                  />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">
                    UCH Self Service
                  </p>
                  <p className="text-gray-500 text-[10px]">
                    University College Hospital
                  </p>
                </div>
              </div>
              <p className="text-xs leading-relaxed text-gray-500 max-w-[200px]">
                Secure digital payment platform for UCH patients, Ibadan.
              </p>
            </div>

            {/* Portal */}
            <div>
              <h5 className="text-white font-semibold text-xs uppercase tracking-widest mb-4">
                Portal
              </h5>
              <ul className="space-y-2.5 text-xs">
                {[
                  ["Home", "/"],
                  ["How it works", "#how-it-works"],
                  ["Services", "#services"],
                ].map(([l, h]) => (
                  <li key={l}>
                    <a href={h} className="hover:text-white transition">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Account */}
            <div>
              <h5 className="text-white font-semibold text-xs uppercase tracking-widest mb-4">
                Account
              </h5>
              <ul className="space-y-2.5 text-xs">
                {[
                  ["Sign in", "/login"],
                  ["Register", "/register"],
                  ["Forgot password", "/forgot-password"],
                ].map(([l, h]) => (
                  <li key={l}>
                    <a href={h} className="hover:text-white transition">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Help */}
            <div>
              <h5 className="text-white font-semibold text-xs uppercase tracking-widest mb-4">
                Help
              </h5>
              <ul className="space-y-2.5 text-xs">
                {[
                  ["Support", "/support"],
                  ["Privacy policy", "/privacy-policy"],
                  ["Terms of use", "/terms-of-use"],
                ].map(([l, h]) => (
                  <li key={l}>
                    <a href={h} className="hover:text-white transition">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <p className="text-xs text-green-500 font-medium">
                  Support 24/7
                </p>
                <a
                  href="mailto:support@uchselfservice.net"
                  className="text-xs hover:text-white transition"
                >
                  support@uchselfservice.net
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} University College Hospital, Ibadan.
              All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <a href="/privacy-policy" className="hover:text-white transition">
                Privacy policy
              </a>
              <a href="/terms-of-use" className="hover:text-white transition">
                Terms of use
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* ── QuickPay Modal ── */}
      {quickPayOpen && (
        <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setQuickPayOpen(false)}
          />
          <div className="relative bg-white w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[85vh] rounded-t-3xl sm:rounded-2xl flex flex-col shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Select Hospital Service
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Search and add services to your cart
                </p>
              </div>
              <button
                onClick={() => setQuickPayOpen(false)}
                className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
              >
                <FaTimes className="text-gray-600 w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="px-6 py-4 space-y-3 shrink-0 border-b border-gray-50">
              {/* Department */}
              <div className="relative" ref={dropdownRef}>
                <div
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                  onClick={() => setDeptOpen(!deptOpen)}
                >
                  <FaSearch className="text-gray-400 w-4 h-4 shrink-0" />
                  <input
                    type="text"
                    placeholder="Filter by department..."
                    value={deptSearch}
                    onChange={(e) => {
                      setDeptSearch(e.target.value);
                      setDeptOpen(true);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 bg-transparent text-sm focus:outline-none text-gray-700 placeholder-gray-400"
                  />
                  <FaChevronDown
                    className={`text-gray-400 w-3 h-3 transition-transform ${deptOpen ? "rotate-180" : ""}`}
                  />
                </div>
                {deptOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-10 max-h-52 overflow-y-auto">
                    {[
                      { id: "", name: "All Departments" },
                      ...filteredDepts,
                    ].map((d) => (
                      <button
                        key={d.id || "all"}
                        onClick={() => {
                          setSelectedDept(d.id);
                          setDeptSearch(d.id ? d.name : "");
                          setDeptOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm hover:bg-green-50 hover:text-[#0d3d2e] transition border-b border-gray-50 last:border-0 ${selectedDept === d.id ? "bg-green-50 text-[#0d3d2e] font-semibold" : "text-gray-700"}`}
                      >
                        {d.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Service search */}
              <div className="relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search services or codes..."
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 placeholder-gray-400"
                />
              </div>
            </div>

            {/* Services list */}
            <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
              {/* Category shortcuts — match ServiceSelectorModal format */}
              {!selectedDept &&
                !serviceSearch &&
                !servicesLoading &&
                filteredServices.length === 0 && (
                  <div className="text-center py-8 space-y-3">
                    <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto">
                      <FaShoppingBag className="text-[#0d3d2e] w-7 h-7" />
                    </div>
                    <p className="text-gray-500 text-sm font-medium">
                      Select a department or search for a service above
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                      {[
                        "Pharmacy",
                        "Laboratory",
                        "Radiology",
                        "Consultations",
                        "Admissions",
                        "Drugs",
                      ].map((hint) => (
                        <button
                          key={hint}
                          onClick={() => setServiceSearch(hint)}
                          className="text-xs bg-gray-100 hover:bg-green-50 hover:text-[#0d3d2e] text-gray-600 px-3 py-1.5 rounded-full transition font-medium"
                        >
                          {hint}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              {servicesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <FaSpinner className="animate-spin text-[#0d3d2e] w-6 h-6" />
                  <span className="ml-3 text-gray-500 text-sm">
                    Loading services...
                  </span>
                </div>
              ) : (serviceSearch || selectedDept) &&
                filteredServices.length === 0 &&
                !servicesLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  No services found. Try a different search.
                </div>
              ) : (
                filteredServices.map((svc) => {
                  const selected = !!selectedItems[svc.id];
                  const needsCustom =
                    svc.fixed_price === "no" &&
                    parseFloat(svc.price || 0) === 0;
                  const price = parseFloat(
                    customPrices[svc.id] || svc.price || 0,
                  );
                  const qty = quantities[svc.id] || 1;
                  return (
                    <div
                      key={svc.id}
                      onClick={() => handleToggleItem(svc)}
                      className={`flex items-center gap-4 px-4 py-3.5 rounded-xl cursor-pointer transition border ${selected ? "border-green-300 bg-green-50" : "border-gray-100 bg-gray-50 hover:bg-gray-100"}`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${selected ? "bg-[#0d3d2e] border-[#0d3d2e]" : "border-gray-300"}`}
                      >
                        {selected && (
                          <FaCheck className="text-white w-2.5 h-2.5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {svc.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {svc.department?.name || "General"}
                        </p>
                        {needsCustom && selected ? (
                          <input
                            type="number"
                            placeholder="Enter amount (₦)"
                            value={customPrices[svc.id] || ""}
                            onChange={(e) => {
                              e.stopPropagation();
                              setCustomPrices((p) => ({
                                ...p,
                                [svc.id]: e.target.value,
                              }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1.5 w-32 px-2.5 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                          />
                        ) : (
                          <p className="text-xs text-green-700 font-semibold mt-0.5">
                            ₦{price.toLocaleString()}
                          </p>
                        )}
                      </div>
                      {selected && (
                        <div
                          className="flex items-center gap-2 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleQty(svc.id, -1)}
                            className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100"
                          >
                            <FaMinus className="w-2.5 h-2.5 text-gray-600" />
                          </button>
                          <span className="w-5 text-center text-sm font-bold text-gray-900">
                            {qty}
                          </span>
                          <button
                            onClick={() => handleQty(svc.id, 1)}
                            className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100"
                          >
                            <FaPlus className="w-2.5 h-2.5 text-gray-600" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-gray-100 px-6 py-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-gray-500">
                    {Object.keys(selectedItems).length} service
                    {Object.keys(selectedItems).length !== 1 ? "s" : ""}{" "}
                    selected
                  </p>
                  <p className="text-lg font-black text-gray-900">
                    ₦{totalCost.toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={handleProceed}
                  disabled={Object.keys(selectedItems).length === 0}
                  className="bg-[#0d3d2e] hover:bg-[#0a3226] disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold px-8 py-3 rounded-xl transition text-sm disabled:cursor-not-allowed"
                >
                  Proceed to Payment →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
